import React, { useEffect, useState } from "react";

import { Box, Slider, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import { FieldLabel } from "../FieldLabel";
import { Input } from "../Input";

export interface RangeSliderProps {
  label: React.ReactNode;
  /**
   * The snap points, ascending, IN THE UNIT SHOWN. The first stop is the open low end and the
   * last the open high end: a handle there means "Any" (no bound), and the bound is `null`.
   * Fixed stops (not data bounds) are what keeps a URL meaning the same slice next month.
   */
  stops: readonly number[];
  /** The current bounds in the shown unit; `null` = open ("Any"). */
  value: [number | null, number | null];
  /** Called with the new bounds (shown unit). Slider drags commit on release, fields on blur / Enter. */
  onChange: (value: [number | null, number | null]) => void;
  /** A stop or a typed value as text, e.g. `$25K`. */
  format: (v: number) => string;
  /** Shown left of the number fields, e.g. `$`. */
  prefix?: string;
  /** Optional faint counts behind the track, one per gap between stops (`stops.length - 1`). */
  histogram?: readonly number[];
  disabled?: boolean;
  /** Prefix for the fields' and handles' accessible names, e.g. "Deal value". */
  ariaLabel?: string;
}

/** The stop nearest a value: where its handle sits when the value was typed between stops. */
const nearestIndex = (stops: readonly number[], v: number): number => {
  let best = 0;
  for (let i = 1; i < stops.length; i += 1) if (Math.abs(stops[i] - v) < Math.abs(stops[best] - v)) best = i;
  return best;
};

/** A typed field: free text, committed to a number (or null = Any) on blur or Enter. */
const BoundField = ({
  value,
  placeholder,
  prefix,
  label,
  disabled,
  onCommit,
}: {
  value: number | null;
  placeholder: string;
  prefix?: string;
  label: string;
  disabled?: boolean;
  onCommit: (v: number | null) => void;
}) => {
  const show = (v: number | null) => (v === null ? "" : String(Math.round(v * 100) / 100));
  const [draft, setDraft] = useState(show(value));
  useEffect(() => setDraft(show(value)), [value]);
  const commit = () => {
    const t = draft.replace(/[$,\s]/g, "");
    if (t === "") return onCommit(null);
    const n = Number(t);
    if (Number.isFinite(n) && n >= 0) onCommit(n);
    else setDraft(show(value));
  };
  return (
    <Input
      placeholder={placeholder}
      value={draft}
      onChange={setDraft}
      onBlur={commit}
      disabled={disabled}
      handleKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === "Enter") commit();
      }}
      startAdornment={
        prefix ? (
          <Typography variant="body3" color="text.secondary">
            {prefix}
          </Typography>
        ) : undefined
      }
      inputProps={{ inputMode: "decimal", "aria-label": label }}
      fullWidth
    />
  );
};

/**
 * A two-handle range control on FIXED snap points (VRG-701): the slider for reach, two number
 * fields for exact values and the keyboard, kept in sync. The track is evenly spaced by STOP, not
 * by value, so roughly log-spaced stops read as a log scale ($1K and $35M both reachable).
 *
 *   - Arrow keys move a handle one stop; Home / End jump to the ends (MUI Slider, step 1 over
 *     stop indexes). Each handle's `aria-valuetext` is the formatted stop, or "Any" at an end.
 *   - A handle at an end means no bound: the field is empty and reads "Any".
 *   - A value typed between two stops is kept exactly; its handle sits on the nearest stop.
 *
 * Layer 1 (`@repo/ui`): absolute px only, never the theme spacing function (working-conventions §47), so it
 * renders the same under both apps' spacing bases.
 */
export const RangeSlider = ({ label, stops, value, onChange, format, prefix, histogram, disabled, ariaLabel }: RangeSliderProps) => {
  const theme = useTheme();
  const brand = theme.palette.brand?.[600] || theme.palette.primary.main;
  const last = stops.length - 1;
  const [lo, hi] = value;
  const toIndex = (): [number, number] => [lo === null ? 0 : nearestIndex(stops, lo), hi === null ? last : nearestIndex(stops, hi)];
  // Local while dragging, committed on release (a URL change per pixel would refetch per pixel).
  const [drag, setDrag] = useState<[number, number] | null>(null);
  const shown = drag ?? toIndex();
  const atLabel = (i: number) => (i === 0 || i === last ? "Any" : format(stops[i]));
  const name = ariaLabel ?? (typeof label === "string" ? label : "Range");
  const peak = histogram && histogram.length ? Math.max(1, ...histogram) : 0;

  const summary =
    lo === null && hi === null
      ? "Any"
      : `${lo === null ? "Any" : format(lo)} – ${hi === null ? "Any" : format(hi)}`;

  return (
    <Stack sx={{ minWidth: 0 }}>
      <FieldLabel
        label={label}
        action={
          <Typography variant="body3" color="text.secondary" data-testid="range-slider-summary">
            {summary}
          </Typography>
        }
      />
      <Box sx={{ position: "relative", px: "10px" }}>
        {peak > 0 && (
          <Box
            aria-hidden
            sx={{ position: "absolute", left: "10px", right: "10px", bottom: "14px", height: "18px", display: "flex", alignItems: "flex-end", gap: "2px" }}
          >
            {histogram!.map((n, i) => (
              <Box key={i} sx={{ flex: 1, height: `${Math.max(n ? 12 : 0, (n / peak) * 100)}%`, backgroundColor: alpha(brand, 0.12), borderRadius: "2px 2px 0 0" }} />
            ))}
          </Box>
        )}
        <Slider
          value={shown}
          min={0}
          max={last}
          step={1}
          marks
          disabled={disabled}
          disableSwap
          onChange={(_, v) => setDrag(v as [number, number])}
          onChangeCommitted={(_, v) => {
            const [a, b] = v as [number, number];
            setDrag(null);
            onChange([a === 0 ? null : stops[a], b === last ? null : stops[b]]);
          }}
          getAriaLabel={(i) => `${name} ${i === 0 ? "minimum" : "maximum"}`}
          getAriaValueText={(i) => atLabel(i)}
          valueLabelDisplay="auto"
          valueLabelFormat={(i) => atLabel(i)}
          sx={{
            color: brand,
            height: "4px",
            py: "14px",
            "& .MuiSlider-thumb": {
              width: "16px",
              height: "16px",
              backgroundColor: theme.palette.background.paper,
              border: `2px solid ${brand}`,
              "&:hover, &.Mui-focusVisible": { boxShadow: `0 0 0 6px ${alpha(brand, 0.16)}` },
            },
            "& .MuiSlider-rail": { backgroundColor: theme.palette.primary[100] ?? theme.palette.divider, opacity: 1 },
            "& .MuiSlider-mark": { width: "2px", height: "2px", borderRadius: "1px", backgroundColor: theme.palette.text.disabled },
            "& .MuiSlider-valueLabel": { fontSize: "12px", borderRadius: "6px", backgroundColor: theme.palette.text.primary },
          }}
        />
      </Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mt: "-4px", mb: "8px" }} aria-hidden>
        <Typography variant="body4" color="text.secondary">
          Any
        </Typography>
        <Typography variant="body4" color="text.secondary">
          {format(stops[last])}+
        </Typography>
      </Stack>
      <Stack direction="row" spacing="8px" alignItems="center">
        <BoundField value={lo} placeholder="Any" prefix={prefix} label={`${name} minimum, exact amount`} disabled={disabled} onCommit={(v) => onChange([v === null || v <= stops[0] ? null : v, hi])} />
        <Typography variant="body3" color="text.secondary" aria-hidden>
          to
        </Typography>
        <BoundField value={hi} placeholder="Any" prefix={prefix} label={`${name} maximum, exact amount`} disabled={disabled} onCommit={(v) => onChange([lo, v])} />
      </Stack>
    </Stack>
  );
};
